import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

export default function About() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.08,
  });

  const trustBadges = [
    { value: '200+', label: 'Engineers Placed' },
    { value: '50+', label: 'Active Clients' },
    { value: '10+', label: 'Industries Served' },
    { value: '18', label: 'Countries Reached' },
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      {/* Subtle background texture */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-primary-500/3 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-emerald-500/3 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header — NEW */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-building-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">About Thoughtic Solutions</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Digital Products + Engineering Teams.
            <br />
            <span className="text-primary-600">One Trusted Partner.</span>
          </motion.h2>
        </motion.div>

        <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-5">
          {/* Image Side — ENHANCED */}
          <motion.div
            initial={{ opacity: 0, x: -60, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8 }}
            className="w-full lg:w-1/2"
          >
            <div className="relative group">
              {/* Glow halo */}
              <div className="absolute -inset-2 "></div>

              {/* Main image card */}
              <div className="">
                <div className="relative h-72 sm:h-80 md:h-96">
                  <img
                    src="about.png"
                    alt="Thoughtic Solutions Team"
                    className="h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  {/* Gradient overlay for text badge */}

                  {/* Bottom-left floating badge */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="absolute bottom-5 left-5 md:bottom-6 md:left-6 px-4 py-2.5 md:px-5 md:py-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg"
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-xs md:text-sm font-bold text-navy-900">Trusted by Industry Leaders</span>
                    </div>
                  </motion.div>

                  {/* Top-right mini stat card */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="absolute top-2 right-5 md:top-2 md:right-6 px-4 py-3 bg-navy-900/90 backdrop-blur-sm rounded-xl shadow-lg"
                  >
                    <div className="text-center">
                      <div className="text-lg md:text-xl font-bold text-white">200+</div>
                      <div className="text-[10px] md:text-xs text-white/70 uppercase tracking-wider font-medium">Engineers</div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Decorative corner accent */}
              <div className="absolute -bottom-3 -right-3 w-24 h-24 bg-primary-500/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-3 -left-3 w-16 h-16 bg-emerald-500/10 rounded-2xl -z-10"></div>
            </div>
          </motion.div>

          {/* Content Side — ENHANCED */}
          <motion.div
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.15 }}
            className="w-full lg:w-1/2 mb-12 md:mb-16"
          >
            <div className="space-y-5 md:space-y-6">
              {[
                "Thoughtic Solutions is a technology company delivering two core capabilities: end-to-end software development and flexible talent solutions.",
                "On the development side, we help businesses turn ideas into market-ready products — from product strategy and UI/UX design to custom engineering, cloud infrastructure, AI integration, and quality assurance. Our full-stack teams work across modern frameworks to deliver secure, scalable applications.",
                "On the talent side, we provide vetted engineers, designers, and specialists who integrate directly into your teams. Whether you need a single developer for a short sprint or a fully managed offshore squad, we match the right professionals in as little as 48 hours.",
              ].map((text, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 25 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.45 + i * 0.1, duration: 0.5 }}
                  className="text-base md:text-lg text-gray-600 leading-relaxed"
                >
                  {text}
                </motion.p>
              ))}

          
            </div>
          </motion.div>
        </div>

        {/* Divider */}
              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={inView ? { opacity: 1, scaleX: 1 } : {}}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="h-px bg-gray-200 origin-left mb-5 md:mb-5"
              />

              {/* Trust badges row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.85, duration: 0.5 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 pt-2"
              >
                {trustBadges.map((badge, idx) => (
                  <motion.div
                    key={badge.label}
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.9 + idx * 0.06, duration: 0.4 }}
                    className="text-center p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-300"
                  >
                    <div className="text-lg md:text-3xl font-bold text-primary-600">{badge.value}</div>
                    <div className="text-[10px] md:text-xl text-gray-500 mt-0.5 font-medium">{badge.label}</div>
                  </motion.div>
                ))}
              </motion.div>
      </div>
      
    </section>
  );
}